// Substep 5-μ.10: Universal Directory console (platform_operator).
//
// Three queues had been accumulating with no human end — capture
// candidates, the research worklist, and correction reports. The
// feedback loop is only real once someone can work them, so this is
// that surface.
//
// Tabs:
//   Directory  — browse/search all shared contacts, edit, see health
//   Capture    — AI/firm discoveries awaiting review
//   Research   — providers clients keep naming that we can't reach,
//                ranked by demand
//   Reports    — firms saying something is wrong; accepting fixes it
//                for every firm at once

import React, { useCallback, useEffect, useState } from "react";
import { C } from "../constants.js";
import { apiFetch } from "../api.js";
import { Spinner } from "../components/Spinner.jsx";

const TABS = [
  { id: "directory", label: "Directory" },
  { id: "capture",   label: "Capture queue" },
  { id: "research",  label: "Research worklist" },
  { id: "reports",   label: "Problem reports" },
];

const REQUEST_TYPE_LABELS = {
  medical_records: "Records", billing: "Billing",
  radiology_images: "Imaging", radiology_reports: "Rad reports",
  emergency_dept: "ER", anesthesia: "Anesthesia",
  pathology_lab: "Lab", physical_therapy: "PT",
  subpoena_legal: "Subpoena", lien_subrogation: "Subro",
  claims: "Claims", general: "General",
};

export function PlatformDirectoryPage({ showNotification }) {
  const [tab, setTab] = useState("directory");
  const [stats, setStats] = useState(null);

  const loadStats = useCallback(async () => {
    try { setStats(await apiFetch("/api/platform-directory/stats")); }
    catch (e) { showNotification?.(`Stats failed: ${e.message}`, "error"); }
  }, [showNotification]);

  useEffect(() => { loadStats(); }, [loadStats]);

  const pendingCount  = stats?.capture?.candidates_by_status?.pending || 0;
  const researchCount = stats?.capture?.candidates_by_status?.needs_research || 0;
  const reportCount   = stats?.health?.open_reports || 0;
  const badge = { capture: pendingCount, research: researchCount, reports: reportCount };

  return React.createElement("div", { className: "fade-in", style: { padding: "32px 40px", maxWidth: "1400px" } },
    React.createElement("div", { style: { marginBottom: "20px" } },
      React.createElement("h1", {
        style: { fontFamily: "'Playfair Display',serif", fontSize: "28px", fontWeight: 600, color: C.navy, marginBottom: "6px" },
      }, "Universal Directory"),
      React.createElement("p", { style: { fontSize: "14px", color: C.textMuted, maxWidth: "860px" } },
        "The shared contact database every firm reads and no firm edits. It grows from AI capture and firm discoveries, and self-corrects from problem reports — an accepted correction fixes it for every firm at once.",
      ),
    ),

    stats && _renderStatBar(stats),

    // Tabs
    React.createElement("div", {
      style: { display: "flex", gap: "4px", borderBottom: `1px solid ${C.warmGray}`, marginBottom: "18px" }
    },
      ...TABS.map(t => React.createElement("button", {
        key: t.id,
        onClick: () => setTab(t.id),
        style: {
          padding: "9px 16px", border: "none", background: "transparent",
          borderBottom: tab === t.id ? `2px solid ${C.gold}` : "2px solid transparent",
          color: tab === t.id ? C.navy : C.textMuted,
          fontWeight: tab === t.id ? 700 : 500, fontSize: "13px",
          cursor: "pointer", display: "flex", alignItems: "center", gap: "7px",
        },
      },
        t.label,
        badge[t.id] > 0 && React.createElement("span", {
          style: {
            minWidth: "19px", padding: "1px 6px", borderRadius: "999px",
            background: t.id === "reports" ? C.errorBg : `${C.gold}22`,
            color: t.id === "reports" ? C.error : C.textDark,
            fontSize: "10px", fontWeight: 700,
          }
        }, badge[t.id]),
      )),
    ),

    tab === "directory" && React.createElement(DirectoryTab, { showNotification, onChange: loadStats }),
    tab === "capture"   && React.createElement(CaptureTab,   { showNotification, onChange: loadStats }),
    tab === "research"  && React.createElement(ResearchTab,  { stats }),
    tab === "reports"   && React.createElement(ReportsTab,   { showNotification, onChange: loadStats }),
  );
}


function _renderStatBar(stats) {
  const h = stats.health || {}, c = stats.capture || {};
  const cells = [
    ["Contacts", h.directory_total ?? "—", null],
    ["Added by capture", c.added_by_auto_capture ?? 0, null],
    ["Proven working", h.proven_working ?? 0, null],
    ["Flagged", h.flagged ?? 0, (h.flagged || 0) > 0 ? C.error : null],
    ["Retired", h.retired ?? 0, null],
  ];
  return React.createElement("div", {
    style: {
      display: "flex", gap: "10px", marginBottom: "18px", flexWrap: "wrap",
    }
  },
    ...cells.map(([label, value, color]) => React.createElement("div", {
      key: label,
      style: {
        flex: "1 1 150px", background: "#fff", borderRadius: "10px",
        border: `1px solid ${C.warmGray}`, padding: "12px 16px",
      }
    },
      React.createElement("div", {
        style: { fontSize: "10px", color: C.textMuted, fontWeight: 700,
                 letterSpacing: "0.06em", textTransform: "uppercase" }
      }, label),
      React.createElement("div", {
        style: { fontSize: "22px", fontWeight: 700, color: color || C.navy, marginTop: "3px" }
      }, String(value)),
    )),
  );
}


// ─── Directory tab ────────────────────────────────────────────
function DirectoryTab({ showNotification, onChange }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [reqType, setReqType] = useState("");
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const p = new URLSearchParams({ include_inactive: "true", limit: "400" });
      if (q.trim()) p.set("q", q.trim());
      if (type) p.set("directory_type", type);
      if (reqType) p.set("request_type", reqType);
      const d = await apiFetch(`/api/directory?${p}`);
      setRows(d.data || []);
    } catch (e) {
      showNotification?.(`Load failed: ${e.message}`, "error");
      setRows([]);
    }
  }, [q, type, reqType, showNotification]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing) return;
    try {
      const { id, ...fields } = editing;
      await apiFetch(`/api/platform-directory/${id}`, {
        method: "PATCH", body: JSON.stringify(fields),
      });
      showNotification?.("Updated for all firms.", "success");
      setEditing(null);
      await load(); onChange?.();
    } catch (e) { showNotification?.(`Save failed: ${e.message}`, "error"); }
  };

  return React.createElement("div", null,
    React.createElement("div", { style: { display: "flex", gap: "8px", marginBottom: "12px", flexWrap: "wrap" } },
      React.createElement("input", {
        type: "text", value: q, onChange: e => setQ(e.target.value),
        placeholder: "Search name, facility, department, city…",
        style: { flex: 1, minWidth: "240px", padding: "8px 12px", borderRadius: "8px",
                 border: `1px solid ${C.warmGray}`, fontSize: "13px" },
      }),
      _select(type, setType, [["", "All types"], ["medical_provider", "Medical"],
        ["insurance_carrier", "Carriers"], ["subrogation_contact", "Subrogation"],
        ["service_vendor", "Vendors"]]),
      _select(reqType, setReqType, [["", "Any request type"],
        ...Object.entries(REQUEST_TYPE_LABELS).map(([k, v]) => [k, v])]),
    ),

    rows === null
      ? React.createElement("div", { style: { padding: "40px", textAlign: "center" } }, React.createElement(Spinner, null))
      : rows.length === 0
        ? _empty("No contacts match those filters.")
        : React.createElement("div", {
            style: { background: "#fff", borderRadius: "10px", border: `1px solid ${C.warmGray}`, overflow: "hidden" }
          },
            ...rows.map((r, i) => React.createElement("div", {
              key: r.id,
              style: {
                padding: "11px 14px",
                borderTop: i === 0 ? "none" : `1px solid ${C.warmGray}`,
                background: r.is_flagged ? `${C.error}08` : (r.is_active ? "#fff" : C.warmWhite),
                display: "flex", alignItems: "flex-start", gap: "10px",
              }
            },
              React.createElement("div", { style: { flex: 1, minWidth: 0 } },
                React.createElement("div", { style: { display: "flex", alignItems: "center", gap: "7px", flexWrap: "wrap" } },
                  React.createElement("span", { style: { fontSize: "13px", fontWeight: 600, color: C.navy } }, r.name),
                  r.facility && React.createElement("span", { style: { fontSize: "12px", color: C.textMuted } }, "· " + r.facility),
                  r.department && React.createElement("span", {
                    style: { fontSize: "10px", padding: "1px 7px", borderRadius: "999px",
                             background: `${C.navy}10`, color: C.navy, fontWeight: 600 }
                  }, r.department),
                  r.is_flagged && React.createElement("span", {
                    style: { fontSize: "10px", padding: "1px 7px", borderRadius: "999px",
                             background: C.errorBg, color: C.error, fontWeight: 700 },
                    title: r.flagged_reason,
                  }, "FLAGGED"),
                  !r.is_active && React.createElement("span", {
                    style: { fontSize: "10px", color: C.textMuted, fontWeight: 600 } }, "· retired"),
                ),
                React.createElement("div", {
                  style: { fontSize: "11px", color: C.textMuted, marginTop: "3px",
                           display: "flex", gap: "12px", flexWrap: "wrap" }
                },
                  r.fax && React.createElement("span", null, "fax ", React.createElement("code", null, r.fax)),
                  r.phone && React.createElement("span", null, "tel ", r.phone),
                  r.email && React.createElement("span", null, r.email),
                  r.submission_method && r.submission_method !== "fax" && React.createElement("span", {
                    style: { color: C.gold, fontWeight: 600 }
                  }, "via " + r.submission_method),
                  r.state && React.createElement("span", null, r.state),
                  r.npi && React.createElement("span", { title: "NPPES verified" }, "NPI " + r.npi),
                  (r.success_count > 0 || r.failure_count > 0) && React.createElement("span", {
                    style: { color: r.failure_count > 0 ? C.error : C.success }
                  }, `${r.success_count}✓ / ${r.failure_count}✗`),
                ),
                (r.request_types || []).length > 0 && React.createElement("div", {
                  style: { display: "flex", gap: "4px", flexWrap: "wrap", marginTop: "4px" }
                },
                  ...(r.request_types || []).map(t => React.createElement("span", {
                    key: t,
                    style: { fontSize: "9px", padding: "1px 6px", borderRadius: "3px",
                             background: `${C.gold}1e`, color: C.textDark, fontWeight: 600 }
                  }, REQUEST_TYPE_LABELS[t] || t)),
                ),
                r.required_forms && React.createElement("div", {
                  style: { fontSize: "11px", color: C.gold, marginTop: "3px" }
                }, "Requires: " + r.required_forms.slice(0, 110)),
              ),
              React.createElement("button", {
                onClick: () => setEditing({ ...r }),
                style: { padding: "3px 11px", borderRadius: "6px", border: `1px solid ${C.warmGray}`,
                         background: "#fff", color: C.textDark, fontSize: "11px", cursor: "pointer", flexShrink: 0 },
              }, "Edit"),
            )),
          ),

    editing && _editModal(editing, setEditing, save),
  );
}


// ─── Capture queue ────────────────────────────────────────────
function CaptureTab({ showNotification, onChange }) {
  const [rows, setRows] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await apiFetch("/api/platform-directory/capture-queue");
      setRows((d.data || []).filter(r => r.status !== "needs_research"));
    } catch (e) { showNotification?.(`Load failed: ${e.message}`, "error"); setRows([]); }
  }, [showNotification]);
  useEffect(() => { load(); }, [load]);

  // Optimistic. This is a work-through-the-list surface: an operator
  // clicks Publish or Reject a dozen times in a row, and awaiting a
  // POST plus a full reload before each row disappears turns a
  // two-minute pass into a chore. Same pattern that made records
  // confirm feel broken.
  const review = async (id, accept) => {
    const prev = rows;
    setRows(rs => (rs || []).filter(r => r.id !== id));
    try {
      await apiFetch(`/api/platform-directory/capture-queue/${id}/review`, {
        method: "POST", body: JSON.stringify({ accept }),
      });
      showNotification?.(accept ? "Published to all firms." : "Rejected.", "success");
      onChange?.();
    } catch (e) {
      setRows(prev);   // server is the truth
      showNotification?.(`Failed: ${e.message}`, "error");
    }
  };

  if (rows === null) return React.createElement("div", { style: { padding: "40px", textAlign: "center" } }, React.createElement(Spinner, null));
  if (!rows.length) return _empty("Nothing waiting. High-confidence captures publish automatically — only ambiguous ones land here.");

  return React.createElement("div", {
    style: { background: "#fff", borderRadius: "10px", border: `1px solid ${C.warmGray}`, overflow: "hidden" }
  },
    ...rows.map((r, i) => React.createElement("div", {
      key: r.id,
      style: { padding: "13px 15px", borderTop: i === 0 ? "none" : `1px solid ${C.warmGray}`,
               display: "flex", gap: "12px", alignItems: "flex-start" }
    },
      React.createElement("div", { style: { flex: 1, minWidth: 0 } },
        React.createElement("div", { style: { display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" } },
          React.createElement("span", { style: { fontSize: "13px", fontWeight: 600, color: C.navy } }, r.name),
          _pill(r.confidence, r.confidence === "high" ? C.success : r.confidence === "medium" ? C.gold : C.textMuted),
          _pill(r.source, C.navy),
          r.distinct_firm_count > 1 && _pill(`${r.distinct_firm_count} firms agree`, C.success),
        ),
        React.createElement("div", { style: { fontSize: "11px", color: C.textMuted, marginTop: "4px", display: "flex", gap: "12px", flexWrap: "wrap" } },
          r.fax && React.createElement("span", null, "fax ", React.createElement("code", null, r.fax)),
          r.phone && React.createElement("span", null, "tel " + r.phone),
          r.email && React.createElement("span", null, r.email),
          r.state && React.createElement("span", null, r.state),
          React.createElement("span", null, `seen ${r.seen_count}x`),
        ),
        r.address && React.createElement("div", { style: { fontSize: "11px", color: C.textMuted, marginTop: "2px" } }, r.address),
        r.discovered_context && React.createElement("div", {
          style: { fontSize: "10px", color: C.textLight, marginTop: "2px" }
        }, "from " + r.discovered_context),
        r.review_note && React.createElement("div", {
          style: { fontSize: "11px", color: C.error, marginTop: "3px" }
        }, r.review_note),
      ),
      React.createElement("div", { style: { display: "flex", gap: "6px", flexShrink: 0 } },
        React.createElement("button", {
          onClick: () => review(r.id, true),
          style: { padding: "5px 13px", borderRadius: "6px", border: "none",
                   background: C.gold, color: "#fff", fontSize: "11px", fontWeight: 600, cursor: "pointer" },
        }, "Publish"),
        React.createElement("button", {
          onClick: () => review(r.id, false),
          style: { padding: "5px 13px", borderRadius: "6px", border: `1px solid ${C.warmGray}`,
                   background: "#fff", color: C.error, fontSize: "11px", cursor: "pointer" },
        }, "Reject"),
      ),
    )),
  );
}


// ─── Research worklist ────────────────────────────────────────
function ResearchTab({ stats }) {
  const list = stats?.capture?.research_worklist || [];
  if (!list.length) return _empty("No outstanding research. Providers clients name that we can't reach yet show up here.");
  return React.createElement("div", null,
    React.createElement("p", { style: { fontSize: "12px", color: C.textMuted, marginBottom: "10px" } },
      "Providers clients keep naming on intake forms that we still have no way to reach. Ranked by demand — the top of this list is where research pays off most.",
    ),
    React.createElement("div", {
      style: { background: "#fff", borderRadius: "10px", border: `1px solid ${C.warmGray}`, overflow: "hidden" }
    },
      ...list.map((r, i) => React.createElement("div", {
        key: i,
        style: { padding: "11px 15px", borderTop: i === 0 ? "none" : `1px solid ${C.warmGray}`,
                 display: "flex", alignItems: "center", gap: "12px" }
      },
        React.createElement("span", {
          style: { minWidth: "34px", fontSize: "16px", fontWeight: 700, color: C.gold, textAlign: "right" }
        }, r.times_named),
        React.createElement("div", { style: { flex: 1 } },
          React.createElement("div", { style: { fontSize: "13px", fontWeight: 600, color: C.navy } }, r.name),
          React.createElement("div", { style: { fontSize: "11px", color: C.textMuted } },
            [r.city, r.state].filter(Boolean).join(", ") || "location unknown",
            r.firms > 1 ? ` · named at ${r.firms} firms` : "",
          ),
        ),
      )),
    ),
  );
}


// ─── Problem reports ──────────────────────────────────────────
function ReportsTab({ showNotification, onChange }) {
  const [rows, setRows] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await apiFetch("/api/platform-directory/reports");
      setRows(d.data || []);
    } catch (e) { showNotification?.(`Load failed: ${e.message}`, "error"); setRows([]); }
  }, [showNotification]);
  useEffect(() => { load(); }, [load]);

  // Optimistic — see the note on the capture queue's review().
  const resolve = async (id, accept) => {
    const prev = rows;
    setRows(rs => (rs || []).filter(r => r.report_id !== id));
    try {
      const r = await apiFetch(`/api/platform-directory/reports/${id}/resolve`, {
        method: "POST", body: JSON.stringify({ accept, apply_value: true }),
      });
      showNotification?.(
        accept
          ? (r.applied_field ? `Fixed ${r.applied_field} for all firms.` : "Accepted.")
          : "Rejected.",
        "success");
      onChange?.();
    } catch (e) {
      setRows(prev);
      showNotification?.(`Failed: ${e.message}`, "error");
    }
  };

  if (rows === null) return React.createElement("div", { style: { padding: "40px", textAlign: "center" } }, React.createElement(Spinner, null));
  if (!rows.length) return _empty("No open reports. When a firm hits a dead fax they report it here, and accepting the fix corrects it for everyone.");

  return React.createElement("div", {
    style: { background: "#fff", borderRadius: "10px", border: `1px solid ${C.warmGray}`, overflow: "hidden" }
  },
    ...rows.map((r, i) => React.createElement("div", {
      key: r.report_id,
      style: { padding: "13px 15px", borderTop: i === 0 ? "none" : `1px solid ${C.warmGray}`,
               display: "flex", gap: "12px", alignItems: "flex-start" }
    },
      React.createElement("div", { style: { flex: 1, minWidth: 0 } },
        React.createElement("div", { style: { display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" } },
          React.createElement("span", { style: { fontSize: "13px", fontWeight: 600, color: C.navy } }, r.contact_name),
          r.department && React.createElement("span", { style: { fontSize: "11px", color: C.textMuted } }, "· " + r.department),
          _pill(r.report_type.replace(/_/g, " "), C.error),
          r.evidence && _pill(r.evidence.replace(/_/g, " "), C.navy),
        ),
        React.createElement("div", { style: { fontSize: "12px", marginTop: "5px" } },
          r.current_value && React.createElement("div", null,
            React.createElement("span", { style: { color: C.textMuted } }, "current: "),
            React.createElement("code", { style: { textDecoration: "line-through", color: C.error } }, r.current_value),
          ),
          r.proposed_value && React.createElement("div", null,
            React.createElement("span", { style: { color: C.textMuted } }, "proposed: "),
            React.createElement("code", { style: { color: C.success, fontWeight: 600 } }, r.proposed_value),
          ),
        ),
        r.detail && React.createElement("div", { style: { fontSize: "11px", color: C.textDark, marginTop: "3px" } }, r.detail),
        React.createElement("div", { style: { fontSize: "10px", color: C.textLight, marginTop: "3px" } },
          `reported by ${r.reported_by_name || "firm " + r.reported_by_firm_id}`,
          r.entry_failure_count > 0 ? ` · ${r.entry_failure_count} observed failures` : "",
        ),
      ),
      React.createElement("div", { style: { display: "flex", gap: "6px", flexShrink: 0 } },
        React.createElement("button", {
          onClick: () => resolve(r.report_id, true),
          title: r.proposed_value ? "Apply this correction for every firm" : "Accept",
          style: { padding: "5px 13px", borderRadius: "6px", border: "none",
                   background: C.gold, color: "#fff", fontSize: "11px", fontWeight: 600, cursor: "pointer" },
        }, "Accept + fix"),
        React.createElement("button", {
          onClick: () => resolve(r.report_id, false),
          style: { padding: "5px 13px", borderRadius: "6px", border: `1px solid ${C.warmGray}`,
                   background: "#fff", color: C.textMuted, fontSize: "11px", cursor: "pointer" },
        }, "Dismiss"),
      ),
    )),
  );
}


// ─── helpers ──────────────────────────────────────────────────
function _pill(text, color) {
  return React.createElement("span", {
    style: { fontSize: "10px", padding: "1px 7px", borderRadius: "999px",
             background: `${color}18`, color, fontWeight: 700 },
  }, text);
}

function _empty(msg) {
  return React.createElement("div", {
    style: { padding: "36px", textAlign: "center", background: "#fff",
             borderRadius: "10px", border: `1px solid ${C.warmGray}`,
             color: C.textMuted, fontSize: "13px" },
  }, msg);
}

function _select(value, onChange, options) {
  return React.createElement("select", {
    value, onChange: e => onChange(e.target.value),
    style: { padding: "8px 10px", borderRadius: "8px", border: `1px solid ${C.warmGray}`,
             fontSize: "13px", background: "#fff", color: C.textDark },
  }, ...options.map(([v, l]) => React.createElement("option", { key: v, value: v }, l)));
}

function _editModal(editing, setEditing, save) {
  const set = (k) => (e) => setEditing(d => ({ ...d, [k]: e.target.value }));
  const F = (label, key, hint) => React.createElement("div", { style: { marginBottom: "9px" } },
    React.createElement("label", {
      style: { display: "block", fontSize: "11px", fontWeight: 600, color: C.navy, marginBottom: "3px" }
    }, label),
    React.createElement("input", {
      type: "text", value: editing[key] || "", onChange: set(key),
      style: { width: "100%", padding: "7px 10px", border: `1px solid ${C.warmGray}`,
               borderRadius: "6px", fontSize: "13px", boxSizing: "border-box" },
    }),
    hint && React.createElement("div", { style: { fontSize: "10px", color: C.textMuted, marginTop: "2px" } }, hint),
  );
  return React.createElement("div", {
    onClick: e => { if (e.target === e.currentTarget) setEditing(null); },
    style: { position: "fixed", inset: 0, background: "rgba(27,42,74,0.6)", display: "flex",
             alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "40px 20px" },
  },
    React.createElement("div", {
      style: { background: "#fff", borderRadius: "12px", padding: "22px", maxWidth: "620px",
               width: "100%", maxHeight: "88vh", overflowY: "auto" },
    },
      React.createElement("h3", {
        style: { fontFamily: "'Playfair Display',serif", fontSize: "19px", color: C.navy, margin: "0 0 4px 0" }
      }, "Edit shared contact"),
      React.createElement("p", { style: { fontSize: "11px", color: C.gold, marginBottom: "14px" } },
        "Changes apply to every firm on the platform immediately."),
      React.createElement("div", { style: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" } },
        F("Name", "name"), F("Facility / campus", "facility"),
        F("Department / desk", "department"), F("Category", "category"),
        F("Fax", "fax"), F("Phone", "phone"),
        F("Email", "email"), F("Submission method", "submission_method", "fax / chartswap / portal / mail_only"),
        F("State", "state"), F("City", "city"),
      ),
      F("Address", "address"),
      F("Required forms", "required_forms", "What must accompany a request"),
      React.createElement("div", {
        style: { display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "14px",
                 paddingTop: "12px", borderTop: `1px solid ${C.warmGray}` }
      },
        React.createElement("button", {
          onClick: () => setEditing(null),
          style: { padding: "8px 16px", borderRadius: "6px", border: `1px solid ${C.warmGray}`,
                   background: "#fff", color: C.textMuted, fontSize: "13px", cursor: "pointer" },
        }, "Cancel"),
        React.createElement("button", {
          onClick: save,
          style: { padding: "8px 20px", borderRadius: "6px", border: "none",
                   background: C.gold, color: "#fff", fontSize: "13px", fontWeight: 600, cursor: "pointer" },
        }, "Save for all firms"),
      ),
    ),
  );
}
